import { useRef, useState, useCallback } from 'react'
import { exportElementPDF, exportElementPNG } from '../utils/pdfExport'
import CertificateOfAchievement from '../components/certificates/CertificateOfAchievement'

const defaultValues = {
  studentName: 'NAKAYIZA KELLY PEACE',
  course: 'Digital Marketing & Paid Advertising',
  grade: 'A+',
  date: '30 June 2026',
  certificateNumber: 'CERT-2026-0058',
  verificationUrl: '',
  qrCodeUrl: '',
  signatureUrl: '/signatures/academy-director.png',
}

export default function CertificateOfAchievementPage() {
  const [values, setValues] = useState(defaultValues)
  const [loading, setLoading] = useState(false)
  const certificateRef = useRef(null)

  const updateValue = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const exportSvg = useCallback(() => {
    const svg = certificateRef.current
    if (!svg) return

    const serializer = new XMLSerializer()
    let source = serializer.serializeToString(svg)

    // Add namespaces and XML declaration for standalone SVG
    if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
      source = source.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"')
    }
    if (!source.match(/^<svg[^>]+xmlns:xlink/)) {
      source = source.replace('<svg', '<svg xmlns:xlink="http://www.w3.org/1999/xlink"')
    }

    const blob = new Blob(['<?xml version="1.0" standalone="no"?>\n', source], {
      type: 'image/svg+xml;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `SMA-Certificate-${values.certificateNumber}.svg`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }, [values.certificateNumber])

  const exportPng = useCallback(async () => {
    if (!certificateRef.current) return
    setLoading(true)
    try {
      await exportElementPNG(
        certificateRef.current,
        `SMA-Certificate-${values.certificateNumber}.png`,
        { scale: 3, backgroundColor: '#FAFAF8' },
      )
    } catch (err) {
      console.error('PNG export failed:', err)
      alert('PNG export failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [values.certificateNumber])

  const exportPdf = useCallback(async () => {
    if (!certificateRef.current) return
    setLoading(true)
    try {
      await exportElementPDF(
        certificateRef.current,
        `SMA-Certificate-${values.certificateNumber}.pdf`,
        { orientation: 'landscape', format: 'a4', backgroundColor: '#FAFAF8' },
      )
    } catch (err) {
      console.error('PDF export failed:', err)
      alert('PDF export failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [values.certificateNumber])

  const inputFields = [
    { key: 'studentName', label: 'Recipient Name' },
    { key: 'course', label: 'Course Title' },
    { key: 'grade', label: 'Grade' },
    { key: 'date', label: 'Date' },
    { key: 'certificateNumber', label: 'Certificate Number' },
    { key: 'verificationUrl', label: 'Verification URL' },
    { key: 'qrCodeUrl', label: 'QR Code URL (optional)' },
    { key: 'signatureUrl', label: 'Signature Image URL (optional)' },
  ]

  return (
    <div className="min-h-screen bg-[#111111] text-white">
      <div className="max-w-7xl mx-auto px-4 py-8 lg:px-8">
        <div className="mb-8">
          <h1 className="font-display text-2xl font-light text-white mb-2">
            Certificate of Achievement
          </h1>
          <p className="font-sans text-sm text-cinema-muted">
            Premium minimalist certificate template. Edit the fields and export to SVG, PNG, or PDF.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Controls */}
          <div className="lg:col-span-4 space-y-4">
            <div className="cinema-glass border border-white/10 rounded-sm p-5">
              <h2 className="font-display text-sm font-medium text-white mb-4">Editable Fields</h2>
              <div className="space-y-3">
                {inputFields.map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs uppercase tracking-wider text-cinema-muted mb-1.5 font-medium">
                      {label}
                    </label>
                    <input
                      type="text"
                      value={values[key]}
                      onChange={(e) => updateValue(key, e.target.value)}
                      className="w-full bg-cinema-graphite border border-white/10 text-white px-3 py-2 text-sm font-sans focus:outline-none focus:border-cinema-glow rounded-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="cinema-glass border border-white/10 rounded-sm p-5">
              <h2 className="font-display text-sm font-medium text-white mb-4">Export</h2>
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={exportSvg}
                  disabled={loading}
                  className="cinema-btn-primary text-xs py-2.5 flex items-center justify-center gap-2"
                >
                  Download SVG
                </button>
                <button
                  onClick={exportPng}
                  disabled={loading}
                  className="cinema-btn-primary text-xs py-2.5 flex items-center justify-center gap-2"
                >
                  {loading ? 'Rendering...' : 'Download PNG (300 DPI)'}
                </button>
                <button
                  onClick={exportPdf}
                  disabled={loading}
                  className="cinema-btn-primary text-xs py-2.5 flex items-center justify-center gap-2"
                >
                  {loading ? 'Rendering...' : 'Download PDF (Print Ready)'}
                </button>
              </div>
              <p className="text-cinema-muted text-xs mt-3 leading-relaxed">
                PNG and PDF exports render at 300 DPI for print-ready output. SVG export preserves full editability.
              </p>
            </div>
          </div>

          {/* Preview */}
          <div className="lg:col-span-8">
            <div className="bg-white p-4 rounded-sm shadow-2xl overflow-hidden">
              <CertificateOfAchievement
                ref={certificateRef}
                studentName={values.studentName}
                course={values.course}
                grade={values.grade}
                date={values.date}
                certificateNumber={values.certificateNumber}
                verificationUrl={values.verificationUrl}
                qrCodeUrl={values.qrCodeUrl || undefined}
                signatureUrl={values.signatureUrl || undefined}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
